import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useTeam } from '@/hooks/useTeam'
import { supabase } from '@/lib/supabase'
import Navbar from '../components/marketing/Navigation'
import Footer from '../components/marketing/Footer'
import Button from '@/components/common/Button'
import Loading from '@/components/common/Loading'

function Pricing() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { team, loading: teamLoading } = useTeam()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleUpgrade = async () => {
    if (!user) {
      navigate('/auth')
      return
    }

    if (!team) {
      navigate('/team-setup')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        throw new Error('No active session. Please sign in again.')
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          teamId: team.id,
          userId: user.id,
          redirectOrigin: window.location.origin
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session')
      }

      window.location.href = data.checkout_url
    } catch (err) {
      setError(err.message)
      setLoading(false)
    }
  }

  if (teamLoading) {
    return <Loading text="Loading..." />
  }

  const isCurrentlyPaid = team?.season_tier === 'paid'
  const seasonEndDate = team?.season_end ? new Date(team.season_end).toLocaleDateString() : null

  return (
    <div className="font-body text-brand-charcoal bg-white min-h-screen flex flex-col">
      <Navbar />

      <section className="relative pt-20 pb-20 flex-grow">
        {/* Background Blobs */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-brand-green/5 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2 -z-10"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-brand-amber/5 rounded-full blur-3xl translate-y-1/2 translate-x-1/4 -z-10"></div>

        <div className="container mx-auto px-6 relative z-10 max-w-5xl">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="font-heading text-4xl lg:text-5xl font-bold mb-6 text-brand-charcoal">
              Free to find scrimmages.<br className="hidden sm:block" />
              <span className="text-brand-green">Pro when you need more.</span>
            </h1>
            <p className="text-xl text-brand-slate max-w-2xl mx-auto mb-4">
              Search and post scrimmages free forever. Upgrade to Pro for unlimited parent emails, AI dispatches, and team management tools.
            </p>
            <p className="text-sm text-brand-slate opacity-75 max-w-2xl mx-auto">
              One-time $49 seasonal payment. No subscriptions, no auto-renewals, no surprises.
            </p>
          </div>

          {/* Current Status */}
          {team && (
            <div className="mb-12 text-center">
              <div className={`inline-block px-8 py-4 rounded-xl shadow-sm border ${isCurrentlyPaid
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-gray-50 border-gray-200 text-gray-700'
                }`}>
                <p className="font-bold text-lg">
                  {isCurrentlyPaid ? (
                    <>
                      ✓ You're on the Pro Plan
                      {seasonEndDate && <span className="ml-2 text-sm opacity-80 font-normal">(until {seasonEndDate})</span>}
                    </>
                  ) : (
                    <>Currently on Free Tier</>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            {/* Free Tier */}
            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-xl relative overflow-hidden group hover:border-brand-green/20 transition-all">
              <div className="mb-8">
                <h2 className="font-heading text-2xl font-bold mb-2 text-brand-charcoal">Free Tier</h2>
                <div className="text-5xl font-black text-brand-charcoal mb-2">
                  $0
                </div>
                <p className="text-brand-slate font-medium">Try before you buy</p>
              </div>

              <ul className="space-y-4 mb-8">
                {[
                  { text: "2 dispatches per month", bold: true },
                  { text: "Up to 25 parent emails", bold: true },
                  { text: "AI dispatch generation" },
                  { text: "Basic wiki (add only)" },
                  { text: "Public team portal (2 sections)" },
                  { text: "Draft note accumulation" },
                  { text: "Scrimmage bulletin (3 active posts)", bold: true },
                  { text: "Saved searches (2) + weekly alerts", bold: true },
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <div className="w-5 h-5 rounded-full bg-brand-green/10 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <svg className="w-3 h-3 text-brand-green" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span className={`text-brand-slate ${item.bold ? 'font-bold text-brand-charcoal' : ''}`}>{item.text}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant="secondary"
                onClick={() => navigate(user ? '/dashboard' : '/auth')}
                className="w-full py-4 rounded-xl font-bold text-lg hover:bg-gray-50 border-2"
              >
                {user ? 'Go to Dashboard' : 'Start Free'}
              </Button>
            </div>

            {/* Pro Tier */}
            <div className="bg-brand-charcoal rounded-3xl p-8 border border-brand-charcoal shadow-2xl relative overflow-hidden text-white transform md:-translate-y-4">
              <div className="absolute top-0 right-0 bg-brand-green text-white px-6 py-2 rounded-bl-2xl font-bold text-sm">
                RECOMMENDED
              </div>

              <div className="mb-8 mt-2">
                <h2 className="font-heading text-2xl font-bold mb-2 text-white">Pro Season</h2>
                <div className="text-5xl font-black text-white mb-2">
                  $49
                </div>
                <p className="text-brand-slate-300 opacity-80 font-medium">One-time payment / 6 months</p>
              </div>

              <ul className="space-y-4 mb-8">
                {[
                  { text: "Unlimited dispatches", bold: true },
                  { text: "Unlimited parent emails", bold: true },
                  { text: "Calendar auto-sync (All Providers)", bold: true },
                  { text: "File uploads (5GB storage)", bold: true },
                  { text: "Full wiki management", bold: true },
                  { text: "Team roster & Snack schedule", bold: true },
                  { text: "Auto-wiki extraction", bold: true },
                  { text: "Unlimited Scrimmage postings", bold: true },
                  { text: "Unlimited Saved searches + instant alerts", bold: true },
                  { text: "No branding", bold: false },
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <div className="w-5 h-5 rounded-full bg-brand-green flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span className={`text-brand-cream/90 ${item.bold ? 'font-bold text-white' : ''}`}>{item.text}</span>
                  </li>
                ))}
              </ul>

              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-sm text-red-200">
                  {error}
                </div>
              )}

              <Button
                variant="primary"
                onClick={handleUpgrade}
                disabled={loading || isCurrentlyPaid}
                className="w-full py-4 rounded-xl font-bold text-lg bg-brand-green hover:bg-brand-green/90 border-transparent"
              >
                {loading ? 'Processing...' : isCurrentlyPaid ? 'Current Plan' : 'Upgrade to Pro'}
              </Button>

              <p className="mt-4 text-xs text-center text-gray-400">
                One-time payment • Renew anytime
              </p>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl font-bold mb-10 text-center text-brand-charcoal">
              Frequently Asked Questions
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                { q: "What's a \"season\"?", a: "A season is 6 months of access to the Pro tier. Most youth sports seasons run 4-6 months, so this gives you plenty of buffer." },
                { q: "Is this a subscription?", a: "Nope! It's a one-time payment. We won't automatically charge you again. When your season ends, you choose if you want to renew." },
                { q: "Can I try before I buy?", a: "Absolutely! The free tier gives you 2 dispatches per month with up to 25 parents. That's enough to verify it works for your team." },
                { q: "What happens when my season ends?", a: "Your data stays safe, but you'll be moved to the free tier limits (2 dispatches/month). You can upgrade again anytime." },
                { q: "Do you offer refunds?", a: "Yes! If you're not satisfied within the first 14 days, email us for a full refund, no questions asked." },
                { q: "Can I use this for multiple teams?", a: "Currently, each team requires its own account. We're working on a multi-team dashboard for power users." },
              ].map((faq, i) => (
                <div key={i} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="font-heading font-bold mb-2 text-brand-charcoal">{faq.q}</h3>
                  <p className="text-brand-slate text-sm leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Pricing
